import React from 'react';
import { RxCross2 } from 'react-icons/rx';
import { MdOutlineArrowDropDown } from 'react-icons/md';
import './DropdownSelectButton.css';

/**
 * DropdownSelectButton
 *
 * Props:
 * @param {string}   label            - floating label
 * @param {string}   selectTxt        - selected value label
 * @param {string}   placeholder      - placeholder when nothing selected
 * @param {function} onClick          - open modal
 * @param {boolean}  crossIcon        - show clear icon when value selected
 * @param {function} crossIconOnlick  - clear handler
 * @param {function} onEditClick      - edit icon click
 * @param {boolean}  error            - red border
 * @param {string}   errorMessage     - error text below
 * @param {boolean}  disabled         - disabled state
 * @param {{ text: string, color: 'green'|'orange'|'blue'|'purple'|'red' }} badge
 *   - optional badge (e.g. Own / Market) shown after the selected text
 */
const DropdownSelectButton = ({
    label = '',
    selectTxt = '',
    placeholder = '',
    onClick,
    crossIcon,
    crossIconOnlick,
    onEditClick,
    error = false,
    errorMessage = '',
    disabled = false,
    badge = null,
}) => {
    const hasValue = !!selectTxt && selectTxt !== placeholder;

    const handleClear = (e) => {
        e.stopPropagation();
        if (crossIconOnlick) crossIconOnlick();
    };

    const handleEdit = (e) => {
        e.stopPropagation();
        if (onEditClick) onEditClick();
    };

    // Same color map as modal badges
    const badgeStyles = {
        green: { background: '#e6f9ec', color: '#16a34a', border: '1px solid #bbf7d0' },
        orange: { background: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa' },
        blue: { background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' },
        purple: { background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe' },
        red: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
    };
    const badgeStyle = badge ? (badgeStyles[badge.color] || badgeStyles.blue) : null;

    // Show the divider only when a value is selected AND badge is present
    const showBadgeDivider = hasValue && badge;

    return (
        <div style={{ position: 'relative', width: '100%' }}>

            {/* ── Trigger Box ── */}
            <div
                className={`__anvCityDropNw __dropSelBtn_wrap ${error ? '__errorDv' : ''} ${disabled ? '__disabledDv' : ''}`}
                onClick={() => !disabled && onClick && onClick()}
            >
                {/* Floating Label */}
                {label && (
                    <label
                        style={{
                            position: 'absolute',
                            top: hasValue ? 0 : error ? '48%' : '50%',
                            left: 6,
                            fontSize: hasValue ? 12 : 14,
                            color: '#666',
                            background: disabled
                                ? hasValue ? '#fff' : '#f2f2f2'
                                : '#fff',
                            padding: hasValue ? '0 4px' : '0 100px 0px 5px',
                            transform: 'translateY(-50%)',
                            transition: 'all 0.2s ease',
                            pointerEvents: 'none',
                        }}
                    >
                        {label}
                    </label>
                )}

                {/* ── Value row: text + badge + vertical divider ── */}
                <div className="__dropSelBtn_valueRow">
                    <span
                        className="__dropSelBtn_valueTxt"
                        style={{ color: hasValue ? '#000' : '#888' }}
                    >
                        {hasValue ? selectTxt : placeholder}
                    </span>
                    {showBadgeDivider && (
                        <span className="__dropSelBtn_vDivider" />
                    )}
                    {/* Ownership badge */}
                    {hasValue && badge && (
                        <span className="__dropSelBtn_badge" style={badgeStyle}>
                            {badge.text}
                        </span>
                    )}

                    {/* Vertical divider between badge area and right icons */}

                </div>

                {/* ── Right icons: clear · caret · divider · edit ── */}
                <span className="_cty_trigger_icons">
                    {crossIcon && !disabled && (
                        <span className="clear-icons" onClick={handleClear}>
                            <RxCross2 size={18} color="black" />
                        </span>
                    )}

                    <span className="_cty_caret" style={{ display: 'flex', alignItems: 'center' }}>
                        <MdOutlineArrowDropDown size={22} />
                    </span>

                    {crossIcon && onEditClick && (
                        <>
                            <span className="_cty_icon_divider" />
                            <span className="_cty_edit_icon" onClick={handleEdit} title="Edit">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                            </span>
                        </>
                    )}
                </span>
            </div>

            {/* Error Message */}
            {error && errorMessage && (
                <div style={{ color: 'var(--invailid-color)', fontSize: 12, marginLeft: 3, marginTop: 3, fontWeight: 500 }}>
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

export default DropdownSelectButton;